/**
 * Attribution capture — preserves ad click identifiers + landing flow
 * across the SPA.
 *
 * Why this module exists:
 *   The siteforge React app receives the user from gigapress.net/siteforge/
 *   with `?gclid=...&fbclid=...&msclkid=...&flow=redesign|new`. GTM's
 *   conversion linker reads gclid out of cookies (`_gcl_aw`) for
 *   `gigapress.net`, but Bing's `_uetmsclkid_` and Meta's `_fbc` only
 *   set reliably when their click ID is present in the URL on the page
 *   that fires the conversion event.
 *
 *   React Router doesn't strip query params, but a guest typically clicks
 *   "Generate for Free" on the WP page and lands on `/` of the React app
 *   with the query intact. They then hit the free-trial form which does
 *   NOT navigate before submitting, so `window.location.search` still has
 *   the click IDs at firing time. To survive ANY in-app redirect (e.g.
 *   logged-in HomeRoute → /dashboard), we mirror the query into
 *   sessionStorage on first paint.
 *
 *   `getSignupSource()` gives the React signup paths a single function
 *   to determine which `sf_signup_source` to record + send to the backend
 *   (`redesign_free` vs `new_free` vs `free_trial`). The backend then
 *   echoes it back at email-confirm time.
 */

const KEY = 'sf_attribution';
const TRACKED_PARAMS = ['gclid', 'fbclid', 'msclkid', 'gbraid', 'wbraid', 'flow', 'site', 'domain', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
type TrackedParam = (typeof TRACKED_PARAMS)[number];

export type AttributionSnapshot = Partial<Record<TrackedParam, string>> & {
  capturedAt?: string;
  landingPath?: string;
};

function readStored(): AttributionSnapshot {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as AttributionSnapshot) : {};
  } catch {
    return {};
  }
}

function writeStored(snap: AttributionSnapshot): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(KEY, JSON.stringify(snap));
  } catch {
    // sessionStorage quota / Safari private mode — nothing we can do.
  }
}

/**
 * Read the current URL's query and merge any tracked params into the
 * stored snapshot. URL-present values WIN over stored values, so a user
 * who arrives with a fresh `gclid` on a return visit gets the new one.
 *
 * Idempotent — safe to call from `main.tsx` on every app mount + from
 * `App.tsx` on route changes.
 */
export function captureFromUrl(): AttributionSnapshot {
  if (typeof window === 'undefined') return {};
  const stored = readStored();
  let dirty = false;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of TRACKED_PARAMS) {
      const v = params.get(key);
      if (v && v !== stored[key]) {
        stored[key] = v.slice(0, 256); // belt-and-braces cap
        dirty = true;
      }
    }
    if (dirty || !stored.capturedAt) {
      stored.capturedAt = stored.capturedAt || new Date().toISOString();
      stored.landingPath = stored.landingPath || window.location.pathname;
      writeStored(stored);
    }
  } catch {
    // ignored
  }
  return stored;
}

export function getAttribution(): AttributionSnapshot {
  return readStored();
}

// Feature 776 (2.06): domain-first deep link. A `/?flow=new&domain=X` marketing
// link stashes the domain in attribution (now a tracked param, so it survives
// the HomeRoute redirect for logged-in users); consume it once to pre-fill the
// domain-claim step / auto-open the domain picker.
export function consumeNewDomainIntent(): { domain: string } | null {
  const a = readStored();
  if (a.flow !== 'new' || !a.domain) return null;
  const domain = a.domain;
  delete (a as any).domain;
  writeStored(a);
  return { domain };
}

/**
 * Map the captured `flow` param to the canonical signup source enum
 * the spec requires:
 *   - `?flow=redesign` → `redesign_free`
 *   - `?flow=new`      → `new_free`
 *   - missing / other  → `free_trial`  (legacy / generic)
 *
 * Caller is responsible for overriding this when the signup is paid
 * (CheckoutPage passes `'paid'` directly).
 */
export function getSignupSource(): 'redesign_free' | 'new_free' | 'free_trial' {
  const a = readStored();
  if (a.flow === 'redesign') return 'redesign_free';
  if (a.flow === 'new' || a.flow === 'new_site' || a.flow === 'newsite') return 'new_free';
  return 'free_trial';
}

/**
 * Click identifiers worth round-tripping to the backend for offline
 * conversion uploads. Returned as a flat object the API layer can
 * spread into a request body.
 */
export function getClickIds(): { gclid?: string; fbclid?: string; msclkid?: string; gbraid?: string; wbraid?: string } {
  // The tab's session snapshot first; when it has no click ID (the ad visit was an earlier session), fall back to
  // the persisted last touch so the click still reaches the backend (2026-09-29).
  const s = readStored();
  const a: Partial<Record<string, string>> = (s.gclid || s.fbclid || s.msclkid || s.gbraid || s.wbraid) ? s : (readTouch(LAST_TOUCH_KEY) || {});
  const out: ReturnType<typeof getClickIds> = {};
  if (a.gclid) out.gclid = a.gclid;
  if (a.fbclid) out.fbclid = a.fbclid;
  if (a.msclkid) out.msclkid = a.msclkid;
  if (a.gbraid) out.gbraid = a.gbraid;
  if (a.wbraid) out.wbraid = a.wbraid;
  return out;
}

/**
 * #ATTRIB-1 (migration 178): first-party UTM parameters + landing domain,
 * round-tripped to the backend the same way getClickIds() round-trips paid
 * click IDs — read from the stored snapshot, only present keys included, so
 * callers can spread the result straight into a request body.
 *
 * `landing_domain` reuses the `domain` tracked param captured by
 * `captureFromUrl` (see TRACKED_PARAMS above). There is no separate
 * hostname capture in the snapshot — `domain` is the only domain-shaped
 * value already being tracked, so for a `?utm_source=...&domain=...`
 * marketing link it doubles as the landing domain. It's absent (and so is
 * this field) when the inbound URL had no `domain=` param, e.g. a plain
 * `?utm_source=...&utm_medium=...&utm_campaign=...` link with no domain tag.
 */
export function getUtm(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_domain?: string;
} {
  const s = readStored();
  // UTM from the tab's session snapshot; when it has none, from the persisted last touch (2026-09-29).
  // landing_domain stays session-only (it is the #776 deep-link `domain` param, not a traffic source).
  const lt = (s.utm_source || s.utm_medium || s.utm_campaign || s.utm_term || s.utm_content) ? null : readTouch(LAST_TOUCH_KEY);
  const a: Partial<Record<string, string>> = lt ? { ...lt, domain: s.domain } : s;
  const out: ReturnType<typeof getUtm> = {};
  if (a.utm_source) out.utm_source = a.utm_source;
  if (a.utm_medium) out.utm_medium = a.utm_medium;
  if (a.utm_campaign) out.utm_campaign = a.utm_campaign;
  if (a.utm_term) out.utm_term = a.utm_term;
  if (a.utm_content) out.utm_content = a.utm_content;
  if (a.domain) out.landing_domain = a.domain;
  return out;
}

/**
 * Read Meta Pixel attribution cookies for the Meta Conversions API
 * dedupe path. Both cookies are first-party + scoped to the firing
 * domain, so the backend can't read them off `req.cookies` reliably
 * (e.g. when the React app is on a subdomain). The frontend mirrors
 * them into the register/confirm-email request body so the server can
 * include them in the CAPI `user_data` payload alongside the hashed
 * email.
 *
 * `_fbp` is set by the Pixel base code on first load (browser id).
 * `_fbc` is set when the URL contains `?fbclid=` and persists across
 * sessions.
 */
export function getMetaCookies(): { fbp?: string; fbc?: string } {
  if (typeof document === 'undefined' || !document.cookie) return {};
  const out: { fbp?: string; fbc?: string } = {};
  try {
    const pairs = document.cookie.split(';');
    for (const raw of pairs) {
      const eq = raw.indexOf('=');
      if (eq < 0) continue;
      const name = raw.slice(0, eq).trim();
      const value = raw.slice(eq + 1).trim();
      if (!value) continue;
      if (name === '_fbp') out.fbp = value.slice(0, 256);
      else if (name === '_fbc') out.fbc = value.slice(0, 256);
    }
  } catch {
    // ignored
  }
  return out;
}

/**
 * Pop the redesign intent off the attribution snapshot. Returns the source
 * site URL when the inbound link was `?flow=redesign&site=<url>` and the
 * intent hasn't been consumed yet; returns null otherwise.
 *
 * Marketing links to siteforge.gigapress.net/?flow=redesign&site=example.com
 * pre-fill the redesign URL in the wizard after signup. Dashboard.tsx calls
 * this on mount and, if a site is returned, auto-opens the domain picker
 * and threads the URL into the post-create navigate as `?importUrl=...`.
 *
 * `site` is cleared on read so closing/reopening the picker doesn't loop.
 * `flow` is left intact — `getSignupSource()` still reads it.
 */
export function consumeRedesignIntent(): { site: string } | null {
  const a = readStored();
  if (a.flow !== 'redesign' || !a.site) return null;
  const site = a.site;
  delete a.site;
  writeStored(a);
  return { site };
}

// ─── Persistent first / last touch (2026-09-29) ──────────────────────────────
// The session snapshot above dies with the browser tab, so an ad click in one visit and a signup in a later one
// arrived with no source (94% of signups had none). Touches persist in localStorage for 90 days:
//   FIRST touch — written once: the visit that brought them (even when it was direct).
//   LAST touch  — the most recent visit that carried a source (a tracked param or an external referrer).
// Both are sent at signup (first_touch / last_touch). The referrer keeps origin + path only, never its query.
const FIRST_TOUCH_KEY = 'sf_first_touch';
const LAST_TOUCH_KEY = 'sf_last_touch';
const TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const TOUCH_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'msclkid', 'gbraid', 'wbraid'] as const;
export type Touch = Partial<Record<(typeof TOUCH_PARAMS)[number], string>> & { at: string; landingPath: string; referrer?: string };

function readTouch(key: string): Touch | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const t = JSON.parse(raw) as Touch;
    const age = Date.now() - Date.parse(t && t.at);
    if (!Number.isFinite(age) || age > TOUCH_TTL_MS) { localStorage.removeItem(key); return null; }
    return t;
  } catch {
    return null;
  }
}

function writeTouch(key: string, t: Touch): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, JSON.stringify(t));
  } catch {
    // quota / private mode — attribution is best-effort.
  }
}

/** document.referrer when it is another site — origin + path only. */
function externalReferrer(): string | undefined {
  try {
    if (typeof document === 'undefined' || !document.referrer) return undefined;
    const r = new URL(document.referrer);
    if (r.host === window.location.host) return undefined;
    return (r.origin + r.pathname).slice(0, 512);
  } catch {
    return undefined;
  }
}

/** Record this page load as a touch (see above). Call once per page load, from main.tsx. */
export function captureTouch(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const t: Touch = { at: new Date().toISOString(), landingPath: window.location.pathname.slice(0, 512) };
    let sourced = false;
    for (const k of TOUCH_PARAMS) {
      const v = params.get(k);
      if (v) { t[k] = v.slice(0, 256); sourced = true; }
    }
    const ref = externalReferrer();
    if (ref) { t.referrer = ref; sourced = true; }
    if (!readTouch(FIRST_TOUCH_KEY)) writeTouch(FIRST_TOUCH_KEY, t);
    if (sourced || !readTouch(LAST_TOUCH_KEY)) writeTouch(LAST_TOUCH_KEY, t);
  } catch {
    // ignored
  }
}

/** Both persisted touches for the signup request body (absent ones omitted). */
export function getTouches(): { first_touch?: Touch; last_touch?: Touch } {
  const out: { first_touch?: Touch; last_touch?: Touch } = {};
  const f = readTouch(FIRST_TOUCH_KEY);
  if (f) out.first_touch = f;
  const l = readTouch(LAST_TOUCH_KEY);
  if (l) out.last_touch = l;
  return out;
}

/**
 * Clear after a successful signup so a new browser session doesn't
 * re-attribute returning users. Called once from the signup post-success
 * path. The dataLayer event has already fired by this point.
 */
export function clearAttribution(): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(KEY);
  } catch {
    // ignored
  }
}
